import React, { useEffect, useMemo, useState } from "react";
import Switch from "react-switch";

function buildRoutes(teams, checkpoints, numRounds) {
  if (checkpoints.length === 0 || teams.length === 0) return [];
  // The pool has to cover BOTH constraints at once: at least `teams.length` checkpoints so no two
  // teams ever land on the same one in the same round, AND at least `numRounds` checkpoints so one
  // team's own journey never revisits a checkpoint it already stood at earlier in its own route.
  // Capping to just teams.length (the old behavior) ignored the second constraint entirely — with
  // more rounds than teams (a very ordinary setup), the modulo below would start wrapping a single
  // team back onto checkpoints it had already visited a few rounds earlier. Only trim the pool when
  // there are honestly more checkpoints than either constraint needs; never trim below that floor.
  const poolSize = Math.min(checkpoints.length, Math.max(teams.length, numRounds));
  const pool = checkpoints.slice(0, poolSize);
  return teams.map((team, t) => {
    const checkpointOrder = Array.from(
      { length: numRounds },
      (_, r) => pool[(t + r) % pool.length].id
    );
    return { teamId: team.id, checkpointOrder };
  });
}

function RouteAssignment({ hunt, updateHunt, goToStep }) {
  const [dragCell, setDragCell] = useState(null);
  const [overCell, setOverCell] = useState(null);
  const checkpoints = hunt.checkpoints || [];
  const teams = hunt.teams || [];
  const manual = !!hunt.manualRoutes;

  // Keyed on the actual checkpoint IDs, not just how many there are — regenerating checkpoints
  // (e.g. toggling "Use suggested checkpoints", or switching venue Setting) gives every checkpoint
  // a brand new ID even when the count stays the same, which otherwise leaves routes silently
  // pointing at IDs that no longer exist anywhere.
  const checkpointIdKey = checkpoints.map((c) => c.id).join(",");

  const autoRoutes = useMemo(
    () => buildRoutes(teams, checkpoints, hunt.numRounds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams.length, checkpointIdKey, hunt.numRounds]
  );

  const checkpointIdSet = useMemo(() => new Set(checkpoints.map((c) => c.id)), [checkpointIdKey]);

  const routes = useMemo(() => {
    const savedRoutesStillValid =
      manual &&
      hunt.routes &&
      hunt.routes.length === teams.length &&
      hunt.routes.every(
        (r) => r.checkpointOrder?.length === hunt.numRounds && r.checkpointOrder.every((id) => checkpointIdSet.has(id))
      );
    return savedRoutesStillValid ? hunt.routes : autoRoutes;
  }, [manual, hunt.routes, autoRoutes, teams.length, hunt.numRounds, checkpointIdSet]);

  useEffect(() => {
    if (hunt.routes !== routes) {
      updateHunt({ routes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  const checkpointName = (id) => checkpoints.find((c) => c.id === id)?.name || "—";

  // Only the specific cells that actually share a checkpoint in the same round are flagged —
  // keyed "teamId:roundIndex" — not the whole team row, so a team's other, non-conflicting
  // rounds don't get painted red too.
  const collidingCells = useMemo(() => {
    const set = new Set();
    for (let r = 0; r < hunt.numRounds; r++) {
      const teamsAtCheckpoint = new Map();
      routes.forEach((route) => {
        const cpId = route.checkpointOrder[r];
        if (!teamsAtCheckpoint.has(cpId)) teamsAtCheckpoint.set(cpId, []);
        teamsAtCheckpoint.get(cpId).push(route.teamId);
      });
      teamsAtCheckpoint.forEach((teamIds) => {
        if (teamIds.length > 1) {
          teamIds.forEach((tid) => set.add(`${tid}:${r}`));
        }
      });
    }
    return set;
  }, [routes, hunt.numRounds]);

  const toggleManual = (checked) => {
    // Turning manual on seeds it from whatever's showing right now, so dragging has a starting point.
    updateHunt({ manualRoutes: checked, routes: checked ? routes : autoRoutes });
  };

  /**
   * Swap whatever checkpoint sits in two cells within the SAME team's row — reorders that
   * team's own route across rounds. Dragging onto a different team's row is a no-op: cross-team
   * swaps aren't allowed here (that would just relocate a collision rather than resolve it).
   */
  const swapCells = (a, b) => {
    if (a.teamIndex !== b.teamIndex) return;
    if (a.roundIndex === b.roundIndex) return;
    const next = routes.map((r) => ({ ...r, checkpointOrder: [...r.checkpointOrder] }));
    const tmp = next[a.teamIndex].checkpointOrder[a.roundIndex];
    next[a.teamIndex].checkpointOrder[a.roundIndex] = next[a.teamIndex].checkpointOrder[b.roundIndex];
    next[a.teamIndex].checkpointOrder[b.roundIndex] = tmp;
    updateHunt({ routes: next });
  };

  const short = checkpoints.length < teams.length;

  if (checkpoints.length === 0 || teams.length === 0) {
    return (
      <div className="studio-step-page" data-tour="studio-step-routes">
        <h2 className="studio-h2">Route assignment</h2>
        <p className="studio-sub">Add checkpoints and teams first to generate routes.</p>
      </div>
    );
  }

  return (
    <div className="studio-step-page" data-tour="studio-step-routes">
      <div className="studio-row-between">
        <h2 className="studio-h2" style={{ marginBottom: 0 }}>
          Route assignment
        </h2>
        <div className="studio-toggle-row">
          <span>Edit routes manually</span>
          <Switch
            checked={manual}
            onChange={toggleManual}
            onColor="#d83307"
            height={22}
            width={44}
            handleDiameter={18}
            checkedIcon={false}
            uncheckedIcon={false}
          />
        </div>
      </div>
      <p className="studio-sub">
        {manual
          ? "Drag a checkpoint onto another round in the same team's row to reorder that team's route. Highlighted cells share a checkpoint with another team in that round."
          : "Routes are assigned automatically and update as teams, checkpoints or rounds change. Turn on manual editing to drag and rearrange them."}
      </p>

      {short && (
        <div className="studio-banner">
          <span className="studio-dot" />
          <div style={{ flex: 1 }}>
            <div className="studio-banner-title">
              {teams.length} teams, {checkpoints.length} locations — collisions are unavoidable
            </div>
            <div className="studio-banner-desc">
              Two teams would share a checkpoint in every round — add {teams.length - checkpoints.length} more location{teams.length - checkpoints.length === 1 ? "" : "s"} to continue.
            </div>
          </div>
          <button className="studio-btn studio-btn--ghost" onClick={() => goToStep(2)}>
            Add locations
          </button>
        </div>
      )}

      <div className="studio-table-wrap" style={{ marginTop: 16 }}>
        <table className="studio-table">
          <thead>
            <tr>
              <th>Team</th>
              {Array.from({ length: hunt.numRounds }, (_, i) => (
                <th key={i}>Round {i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {routes.map((route, teamIndex) => {
              const team = teams.find((t) => t.id === route.teamId);
              return (
                <tr key={route.teamId}>
                  <td className="studio-table-strong">{team?.name}</td>
                  {route.checkpointOrder.map((cpId, roundIndex) => {
                    const isDragging = dragCell?.teamIndex === teamIndex && dragCell?.roundIndex === roundIndex;
                    // A cell is only a valid drop target if it's in the SAME row the drag started
                    // from — cross-team drops are disallowed (see swapCells).
                    const isSameRowDrag = dragCell != null && dragCell.teamIndex === teamIndex;
                    const isOver = isSameRowDrag && overCell?.teamIndex === teamIndex && overCell?.roundIndex === roundIndex;
                    const collides = collidingCells.has(`${route.teamId}:${roundIndex}`);
                    return (
                      <td
                        key={roundIndex}
                        className={
                          "studio-route-cell" +
                          (isOver ? " studio-route-cell--over" : "") +
                          (collides ? " studio-route-cell--collide" : "")
                        }
                        onDragOver={(e) => {
                          if (!manual || !isSameRowDrag) return;
                          // Always allow the drop; whether it does anything is decided in onDrop.
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }}
                        onDragEnter={(e) => {
                          if (!manual || !isSameRowDrag) return;
                          e.preventDefault();
                          setOverCell({ teamIndex, roundIndex });
                        }}
                        onDragLeave={() =>
                          setOverCell((c) => (c?.teamIndex === teamIndex && c?.roundIndex === roundIndex ? null : c))
                        }
                        onDrop={(e) => {
                          if (!manual) return;
                          e.preventDefault();
                          let source = dragCell;
                          const raw = e.dataTransfer.getData("text/plain");
                          if (raw) {
                            try {
                              source = JSON.parse(raw);
                            } catch {
                              // keep state-based fallback
                            }
                          }
                          if (source && source.teamIndex === teamIndex) {
                            swapCells(source, { teamIndex, roundIndex });
                          }
                          setDragCell(null);
                          setOverCell(null);
                        }}
                        title={manual ? "Drag onto another round in this same team's row to reorder" : undefined}
                      >
                        {/* Table cells have unreliable native drag support in some browsers (notably Safari) —
                            the draggable element is this inner div, not the <td> itself. */}
                        <div
                          className={
                            "studio-route-cell-inner" +
                            (manual ? " studio-route-cell-inner--manual" : "") +
                            (isDragging ? " studio-route-cell-inner--dragging" : "")
                          }
                          draggable={manual}
                          onDragStart={(e) => {
                            if (!manual) return;
                            setDragCell({ teamIndex, roundIndex });
                            e.dataTransfer.effectAllowed = "move";
                            // Some browsers (Firefox) require setData for the drag to proceed at all.
                            e.dataTransfer.setData("text/plain", JSON.stringify({ teamIndex, roundIndex }));
                          }}
                          onDragEnd={() => {
                            setDragCell(null);
                            setOverCell(null);
                          }}
                        >
                          {manual && <i className="fa-solid fa-grip-vertical studio-route-cell-handle" />}
                          {checkpointName(cpId)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {collidingCells.size > 0 && (
        <div className="studio-hint" style={{ marginTop: 10 }}>
          <span className="studio-swatch studio-swatch--collide" /> Sharing a checkpoint with another team
        </div>
      )}
    </div>
  );
}

export default RouteAssignment;
